import { Component, computed, inject, signal } from '@angular/core';
import { form, FormField, maxLength, required, submit } from '@angular/forms/signals';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';

import { TagComponent } from '@app/shared/components/tag/tag.component';
import { DialogModeEnum } from '@app/shared/types/enums/dialog-mode.enum';
import { Tag, TagFormDialogData, TagFormModel } from '@app/shared/types/models/tag/tag.model';
import { UiColorPickerComponent } from '@app/shared/ui/color-picker/ui-color-picker.component';
import { UiDialogComponent } from '@app/shared/ui/dialog/ui-dialog.component';
import { UiInputComponent } from '@app/shared/ui/input/ui-input.component';
import { TranslatePipe } from '@ngx-translate/core';

export type TagFormDialogResult = TagFormModel;

const NAME_MAX_LENGTH = 50;
const DESCRIPTION_MAX_LENGTH = 100;

@Component({
  selector: 'proffeo-tag-form-dialog',
  imports: [
    MatDialogModule,
    TranslatePipe,
    UiDialogComponent,
    FormField,
    TagComponent,
    UiButtonComponent,
    UiColorPickerComponent,
    UiInputComponent
  ],
  templateUrl: './tag-form-dialog.component.html'
})
export class TagFormDialogComponent {
  private readonly dialogRef: MatDialogRef<TagFormDialogComponent> = inject(MatDialogRef<TagFormDialogComponent>);
  private readonly dialogData: TagFormDialogData = inject(MAT_DIALOG_DATA);

  protected readonly mode = this.dialogData?.mode ?? DialogModeEnum.CREATE;
  protected readonly descriptionMaxLength = DESCRIPTION_MAX_LENGTH;

  //TODO set the same maxlength for description and name in form fields
  protected readonly model = signal<TagFormModel>(this.toFormModel(this.dialogData?.projectTagToEdit));
  protected readonly form = form(this.model, path => {
    required(path.name, { message: 'ui-forms-this-field-is-required' });
    maxLength(path.name, NAME_MAX_LENGTH, { message: 'ui-forms-the-maximum-value-is' });
    maxLength(path.description, DESCRIPTION_MAX_LENGTH);
    required(path.hexColor);
  });

  protected readonly previewTag = computed((): Tag | undefined => {
    const value = this.model();
    return value.hexColor && value.name ? new Tag(value) : undefined;
  });

  public save(): void {
    void submit(this.form, {
      action: async () => {
        const result: TagFormDialogResult = this.model();
        this.dialogRef.close(result);
      }
    });
  }

  private toFormModel(tag: Tag | undefined): TagFormModel {
    return {
      id: tag?.id ?? '',
      hexColor: tag?.hexColor ?? '',
      name: tag?.name ?? '',
      description: tag?.description ?? ''
    };
  }
}
